import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { ChevronDown, ChevronUp, Copy, Check } from 'react-feather';
import { useTranslations } from 'use-intl';
import { useInstructionHistory, type InstructionVersion } from '@/hooks/useInstructionHistory';
import { BRAND_COLORS, GRAY_COLORS } from '@/UIlibrary/globalTheme';

interface VersionHistoryPanelProps {
	entityId: string;
	contentKey: string;
}

function formatVersionDate(createdAt: string): string {
	try {
		const date = new Date(createdAt);
		return date.toLocaleString(undefined, {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	} catch {
		return createdAt;
	}
}

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
	entityId,
	contentKey
}) => {
	const t = useTranslations('VersionHistory');
	const { versions, isLoading, loadHistory, copyVersionContent } = useInstructionHistory();
	const [isExpanded, setIsExpanded] = useState(false);
	const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);

	useEffect(() => {
		if (entityId) {
			loadHistory(entityId);
		}
	}, [entityId, loadHistory]);

	const handleCopy = (version: InstructionVersion) => {
		copyVersionContent(version.content, contentKey);
		setCopiedId(version.id);
		setTimeout(() => setCopiedId(null), 2000);
	};

	return (
		<div className="w-full border border-gray-200 rounded-lg bg-white mt-4">
			<button
				type="button"
				onClick={() => setIsExpanded(!isExpanded)}
				className="w-full flex items-center justify-between p-4 transition-colors hover:bg-gray-50"
			>
				<Typography type="text" weight="semibold">
					{t('Title')} ({versions.length})
				</Typography>
				{isExpanded ? (
					<ChevronUp size={20} className="text-gray-600" />
				) : (
					<ChevronDown size={20} className="text-gray-600" />
				)}
			</button>

			{isExpanded && (
				<div className="border-t border-gray-200 p-4 space-y-3">
					{isLoading ? (
						<Typography type="text" color="subtitle">
							{t('Loading')}
						</Typography>
					) : versions.length === 0 ? (
						<Typography type="text" color="subtitle">
							{t('NoVersions')}
						</Typography>
					) : (
						versions.map((version) => (
							<div key={version.id} className="border border-gray-200 rounded-lg overflow-hidden">
								<div className="flex items-center justify-between p-3 bg-gray-50">
									<Typography type="text" weight="medium">
										v{version.version_number} — {formatVersionDate(version.created_at)}
									</Typography>
									<button
										type="button"
										onClick={() => handleCopy(version)}
										className="p-1.5 hover:bg-gray-200 rounded transition-colors"
										aria-label={t('CopyVersion')}
									>
										{copiedId === version.id ? (
											<Check size={16} color={BRAND_COLORS.brand500} />
										) : (
											<Copy size={16} color={GRAY_COLORS.gray600} />
										)}
									</button>
								</div>
								{expandedVersionId === version.id && (
									<div className="p-3 border-t border-gray-200">
										<TextArea
											label=""
											value={
												typeof version.content[contentKey] === 'string'
													? (version.content[contentKey] as string)
													: JSON.stringify(version.content[contentKey] ?? '')
											}
											onChange={() => {}}
											disabled
											rows={8}
										/>
									</div>
								)}
								<button
									type="button"
									onClick={() =>
										setExpandedVersionId(expandedVersionId === version.id ? null : version.id)
									}
									className="w-full p-2 text-left text-sm text-gray-600 hover:bg-gray-100"
								>
									{expandedVersionId === version.id ? t('HideContent') : t('ViewContent')}
								</button>
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
};
